import { ScreenRecord } from '@/feedback/components/Uploader/screenrecord';
import { ScreenShot } from '@/feedback/components/Uploader/screenshot';
import { formatPhone } from '@/feedback/constants';
import * as $18n from '@alife/lazada-i18n';
import { axios } from '@alife/workstation-utils';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useBoolean, useRequest, useSetState, useUpdateEffect } from 'ahooks';
import { Flex, GetProp, Modal, Upload, UploadFile, UploadProps, message } from 'antd';
import { UploadChangeParam } from 'antd/es/upload/interface';
import React, { useState } from 'react';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];
interface IPreview {
  show: boolean;
  url?: string;
  title?: string;
  type?: 'image' | 'video';
}

export const FileUploader = ({ phone, email, onFieldChange }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, { setTrue: startUpload, setFalse: endUpload }] = useBoolean(false);
  const [preview, setPreview] = useSetState<IPreview>({ show: false });

  const { runAsync } = useRequest(
    async () => {
      const query: { email?: string; phone?: string; nationCode?: string } = {
        email,
        ...formatPhone(phone),
      };

      const response = await axios('mtop.lazada.cco.feedback.unlogin.portal.oss.getConfig', {
        data: { query },
        method: 'POST',
        noUrlQuery: true,
      });
      if (response?.status !== 200 || !response?.data?.result) return false;
      return response?.data?.result;
    },
    { manual: true },
  );

  useUpdateEffect(() => {
    onFieldChange(fileList.map((file) => file.url));
  }, [fileList]);

  const preCheck = () => {
    if (uploading) return false;
    // 邮箱手机号校验
    if (!phone && !email) {
      message.error(/*#__PURE__*/$18n.get(
        'asc-fb.feedback-form.account.hint',
        'Please enter you login email or phone number'
      ));
      return false;
    }
    return true;
  };

  const beforeUpload = (file: FileType) => {
    const check = preCheck();
    if (!check) return false;

    // 类型检测
    if (
      !['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/bmp'].includes(file.type) &&
      !file.type.startsWith('video')
    ) {
      message.error(/*#__PURE__*/$18n.get(
        'asc-fb.feedback-form.upload-type-error',
        'Wrong file type, please upload again'
      ));
      return false;
    }
    // 文件大小检测 - 50M
    if (file.size / 1024 / 1024 > 50) {
      message.error(
        /*#__PURE__*/$18n.get(
          'asc-fb.feedback-form.upload-size-error',
          'File size should less then 50m, please upload again'
        ),
      );
      return false;
    }
    return true;
  };

  const onChange = async (info: UploadChangeParam) => {
    if (info.file.status === 'uploading') {
      startUpload();
      const onError = () => {
        message.error(
          /*#__PURE__*/$18n.get(
            'asc-fb.feedback-form.upload-failed-message',
            'The file can not be upload due to system error!'
          ),
        );
        endUpload();
      };

      try {
        const config = await runAsync();

        const fileName = info.file.name;
        const { host, keyPrefix, signature, policy, accessId } = config;
        const suffix = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
        // Key should not be encoded due to encoding policy in OSS system
        const key = keyPrefix + [fileName.replace(suffix, ''), Date.now()].join('-') + suffix;
        // Encoded for saving only
        const encodedKey =
          keyPrefix + [encodeURIComponent(fileName).replace(suffix, ''), Date.now()].join('-') + suffix;
        info.file.url = `${host}/${encodedKey}`;

        const xhr: XMLHttpRequest = new XMLHttpRequest();
        const formData = new FormData();
        formData.append('getTokenUrl', 'mtop.lazada.cco.feedback.unlogin.portal.oss.getConfig');
        formData.append('bucketId', 'autofeedback');
        formData.append('key', key);
        formData.append('policy', policy);
        formData.append('OSSAccessKeyId', accessId);
        formData.append('success_action_status', '200');
        formData.append('signature', signature);
        formData.append('x-oss-object-acl', 'public-read');
        formData.append('file', info.file.originFileObj as any);

        xhr.onerror = (e) => {
          onError();
        };

        xhr.onload = () => {
          if (xhr.status < 200 || xhr.status >= 300) {
            onError();
          } else {
            info.file.status = 'done';
            setFileList(info.fileList);
            endUpload();
          }
        };

        xhr.open('POST', host, true);

        if ('withCredentials' in xhr) {
          xhr.withCredentials = true;
        }

        const headers = {};
        if (headers['X-Requested-With'] !== null) {
          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        }

        xhr.send(formData);
        return;
      } catch (err) {
        onError();
        return;
      }
    }
    if (info.file.status === 'removed') {
      setFileList(info.fileList);
    }
  };

  const handleScreenShot = async (file) => {
    file.status = 'uploading';
    await onChange({
      file,
      fileList: [...fileList, file],
    });
  };

  const handlePreview = async (file: UploadFile) => {
    setPreview({
      show: true,
      url: file.url,
      title: file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1),
      type: file?.type?.includes('video/') ? 'video' : 'image',
    });
  };

  return <>
    <Flex gap={8} wrap>
      <Upload
        style={{ flex: 'none' }}
        listType="picture-card"
        accept="image/png, image/jpg, image/jpeg, image/gif, image/bmp, video/*"
        withCredentials={true}
        maxCount={3}
        beforeUpload={beforeUpload}
        onChange={onChange}
        onPreview={handlePreview}
        fileList={fileList}
        disabled={uploading}
      >
        {fileList.length < 3 ? (
          <button style={{ border: 0, background: 'none' }} type="button">
            {uploading ? <LoadingOutlined /> : <PlusOutlined />}
            <div style={{ marginTop: 8 }}>{/*#__PURE__*/$18n.get('asc-fb.feedback-form.upload-title', 'File Upload')}</div>
          </button>
        ) : null}
      </Upload>
      {fileList.length < 3 ? (
        <ScreenShot
          uploading={uploading}
          preCheck={preCheck}
          beforeUpload={beforeUpload}
          handleUpload={handleScreenShot}
        />
      ) : null}
      {fileList.length < 3 ? (
        <ScreenRecord
          uploading={uploading}
          preCheck={preCheck}
          beforeUpload={beforeUpload}
          handleUpload={handleScreenShot}
        />
      ) : null}
    </Flex>

    <Modal
      open={preview.show}
      title={preview.title}
      footer={null}
      destroyOnClose={true}
      width={850}
      onCancel={() => setPreview({ show: false, url: '', title: '' })}
    >
      {preview.type === 'video' ? (
        <video style={{ width: 800 }} controls autoPlay>
          <source src={preview.url} />
        </video>
      ) : (
        <img alt="preview" style={{ width: 800 }} src={preview.url} />
      )}
    </Modal>
  </>;
};
